//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { AnnouncementDto, AnnouncementType } from '../Sidebar/SidebarTypes';

interface GlobalAnnouncementBannerProps {
  announcements: AnnouncementDto[];
}

const typeIconMap: Record<AnnouncementType, React.ReactNode> = {
  maintenance: (
    <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#F1C21B' }} />
  ),
  update: <InfoOutlinedIcon sx={{ fontSize: 18, color: '#78A9FF' }} />,
  general: <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#42BE65' }} />,
};

function formatScheduledTime(expiresAt: string): string {
  try {
    const date = new Date(expiresAt);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return expiresAt;
  }
}

const GlobalAnnouncementBanner: React.FC<GlobalAnnouncementBannerProps> = ({
  announcements,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!announcements || announcements.length === 0) {
    return null;
  }

  const safeIndex = Math.min(currentIndex, announcements.length - 1);
  const announcement = announcements[safeIndex];
  const hasMultiple = announcements.length > 1;

  const handleBack = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, announcements.length - 1));
  };

  return (
    <div
      data-testid="global-announcement-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '40px',
        backgroundColor: '#001D6C',
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1400,
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 500,
        gap: '12px',
        padding: '0 16px',
        boxSizing: 'border-box',
      }}
    >
      {/* Navigation back arrow */}
      {hasMultiple && (
        <button
          data-testid="announcement-nav-back"
          onClick={handleBack}
          disabled={safeIndex === 0}
          style={{
            background: 'none',
            border: 'none',
            color: '#FFFFFF',
            cursor: safeIndex === 0 ? 'default' : 'pointer',
            opacity: safeIndex === 0 ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            padding: '2px',
            flexShrink: 0,
          }}
          aria-label="Previous announcement"
        >
          <ChevronLeft size={16} />
        </button>
      )}

      {/* Type icon */}
      <span
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
        data-testid="announcement-type-icon"
      >
        {typeIconMap[announcement.type] || typeIconMap.maintenance}
      </span>

      {/* Title + Message */}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        data-testid="announcement-message"
      >
        {announcement.title && (
          <span style={{ fontWeight: 700, marginRight: '6px' }}>
            {announcement.title}
          </span>
        )}
        {announcement.message}
      </span>

      {/* Scheduled end time */}
      {announcement.expiresAt && (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            opacity: 0.85,
            fontSize: '12px',
          }}
          data-testid="announcement-schedule"
        >
          <ScheduleIcon sx={{ fontSize: 14 }} />
          Until {formatScheduledTime(announcement.expiresAt)}
        </span>
      )}

      {/* Counter + Navigation next arrow */}
      {hasMultiple && (
        <>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              fontSize: '11px',
              fontWeight: 600,
            }}
            data-testid="announcement-nav-counter"
          >
            {safeIndex + 1}/{announcements.length}
          </span>
          <button
            data-testid="announcement-nav-next"
            onClick={handleNext}
            disabled={safeIndex === announcements.length - 1}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              cursor:
                safeIndex === announcements.length - 1 ? 'default' : 'pointer',
              opacity: safeIndex === announcements.length - 1 ? 0.4 : 1,
              display: 'flex',
              alignItems: 'center',
              padding: '2px',
              flexShrink: 0,
            }}
            aria-label="Next announcement"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}
    </div>
  );
};

export default GlobalAnnouncementBanner;
